import { defineStore } from "pinia";
import { shallowRef, ref } from "vue";
import {
  fetchWindGrid,
  type WindGrid,
  type ViewportBounds,
} from "@/services/windData.service";
import { createLatestRequest } from "@/utils/latestRequest";

export interface WindSnapshot {
  grid: WindGrid;
  bounds: ViewportBounds;
  zoom: number;
  fetchedAt: number;
}

export const useWindStore = defineStore("wind", () => {
  const snapshot = shallowRef<WindSnapshot | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const requests = createLatestRequest<WindGrid | null>();
  let version = 0;

  /** Reuse a covering wind grid or fetch the latest requested map area. */
  async function load(
    bounds: ViewportBounds,
    zoom: number,
  ): Promise<WindGrid | null> {
    const request = ++version;
    const cached = snapshot.value;
    if (
      cached &&
      Date.now() - cached.fetchedAt < 3600000 &&
      Math.abs(zoom - cached.zoom) <= 1 &&
      bounds.south >= cached.bounds.south &&
      bounds.north <= cached.bounds.north &&
      bounds.west >= cached.bounds.west &&
      bounds.east <= cached.bounds.east
    ) {
      requests.invalidate();
      loading.value = false;
      error.value = null;
      return cached.grid;
    }

    loading.value = true;
    error.value = null;
    try {
      const key = JSON.stringify([bounds, zoom]);
      const grid = await requests.run(key, () => fetchWindGrid(bounds, zoom));
      if (request !== version || grid === undefined) return null;
      if (!grid) {
        error.value = "Wind data unavailable";
        return null;
      }
      snapshot.value = {
        grid,
        zoom,
        fetchedAt: Date.now(),
        bounds: {
          south: grid.lats[0],
          north: grid.lats[grid.lats.length - 1],
          west: grid.lons[0],
          east: grid.lons[grid.lons.length - 1],
        },
      };
      return grid;
    } catch (cause) {
      if (request === version)
        error.value =
          cause instanceof Error ? cause.message : "Wind data unavailable";
      return null;
    } finally {
      if (request === version) loading.value = false;
    }
  }

  return { snapshot, loading, error, load };
});
